import type { ErrorCopy } from '../../api/errors';
import type { PortalLocale } from '../../lib/locale';

interface BusinessDayCloseCopy {
  sectionTitle: string;
  sectionDescription: string;
  loading: string;
  businessDayLabel: string;
  currentDay: string;
  priorDayHint: string;
  daysLoadFailed: { title: string; message: string };
  status: Record<'open' | 'closed' | 'reopened', string>;
  closedAt: (at: string) => string;
  dayWindow: string;
  closeAction: string;
  reopenedTitle: string;
  drawers: string;
  noneOpened: string;
  drawerSummary: (reconciled: number, open: number) => string;
  variance: string;
  nothingReconciled: string;
  nothingReconciledHint: string;
  someReconciledHint: (reconciled: number) => string;
  allReconciledHint: string;
  rounding: string;
  roundingHint: string;
  exact: string;
  short: string;
  over: string;
  notReconciled: string;
  drawerCloseRequired: string;
  drawerCloseRequiredBody: (open: number) => string;
  guestWorkRequired: string;
  guestWorkRequiredBody: (ordinaryTabs: number, awaitingPayment: number) => string;
  confirmTitle: (date: string) => string;
  confirmBody: string;
  confirmReopen: string;
  confirmAction: string;
  confirmCancel: string;
  closedTitle: string;
  closedBody: string;
  reopenReason: string;
  reopenReasonDescription: string;
  reopenAction: string;
  lateTitle: string;
  lateDescription: string;
  arrivedLate: string;
  recordedAfterClose: string;
  lateMeta: (state: string, by: string | null, at: string) => string;
  landedWhat: (what: string) => string;
  closedNotice: { title: string; message: string };
  reopenedNotice: { title: string; message: string };
  problem: (action: 'close' | 'reopen', code?: string) => ErrorCopy;
}

const ENGLISH_PROBLEMS = {
  noConnection: {
    title: 'No Connection',
    message: 'Nothing changed. Check the connection and try again.',
  },
  drawerOpen: {
    title: 'Drawer Close Required',
    message: 'Close and reconcile every cash drawer before closing the business day.',
  },
  guestWork: {
    title: 'Tabs or Payments Still Open',
    message:
      'Resolve every ordinary tab and every bill awaiting payment. Only an explicitly across-day tab for a known customer may remain open.',
  },
  alreadyClosed: {
    title: 'Business Day Already Closed',
    message: 'Someone closed it while this screen was open. Refresh to see the current status.',
  },
  notClosed: {
    title: 'Business Day Still Open',
    message: 'It is not closed, so there is nothing to reopen.',
  },
  closeFailed: {
    title: 'Business Day Close Failed',
    message: 'The business day was not closed. Refresh the page and try again.',
  },
  reopenFailed: {
    title: 'Business Day Reopen Failed',
    message: 'The business day was not reopened. Refresh the page and try again.',
  },
} satisfies Record<string, ErrorCopy>;

const SPANISH_PROBLEMS = {
  noConnection: {
    title: 'Sin conexión',
    message: 'No se cambió nada. Revisá la conexión e intentá de nuevo.',
  },
  drawerOpen: {
    title: 'Cierre de caja pendiente',
    message: 'Cerrá y conciliá cada caja antes de cerrar el día de operación.',
  },
  guestWork: {
    title: 'Hay cuentas o pagos pendientes',
    message:
      'Resolvé cada cuenta ordinaria y cada cuenta de consumo pendiente de pago. Solo puede quedar abierta una cuenta entre días asignada expresamente a un cliente conocido.',
  },
  alreadyClosed: {
    title: 'Día de operación ya cerrado',
    message: 'Alguien lo cerró mientras esta pantalla estaba abierta. Actualizá para ver el estado actual.',
  },
  notClosed: {
    title: 'Día de operación abierto',
    message: 'Todavía no está cerrado, así que no hay nada que reabrir.',
  },
  closeFailed: {
    title: 'No se pudo cerrar el día de operación',
    message: 'El día de operación no se cerró. Actualizá la pantalla e intentá de nuevo.',
  },
  reopenFailed: {
    title: 'No se pudo reabrir el día de operación',
    message: 'El día de operación no se reabrió. Actualizá la pantalla e intentá de nuevo.',
  },
} satisfies Record<string, ErrorCopy>;

function englishProblem(action: 'close' | 'reopen', code?: string): ErrorCopy {
  if (code === 'network') return ENGLISH_PROBLEMS.noConnection;
  if (code === 'drawer_still_open') return ENGLISH_PROBLEMS.drawerOpen;
  if (code === 'ordinary_tabs_still_open' || code === 'payment_still_due' || code === 'unresolved_close_work')
    return ENGLISH_PROBLEMS.guestWork;
  if (code === 'day_already_closed') return ENGLISH_PROBLEMS.alreadyClosed;
  if (code === 'day_not_closed') return ENGLISH_PROBLEMS.notClosed;
  return action === 'close' ? ENGLISH_PROBLEMS.closeFailed : ENGLISH_PROBLEMS.reopenFailed;
}

function spanishProblem(action: 'close' | 'reopen', code?: string): ErrorCopy {
  if (code === 'network') return SPANISH_PROBLEMS.noConnection;
  if (code === 'drawer_still_open') return SPANISH_PROBLEMS.drawerOpen;
  if (code === 'ordinary_tabs_still_open' || code === 'payment_still_due' || code === 'unresolved_close_work')
    return SPANISH_PROBLEMS.guestWork;
  if (code === 'day_already_closed') return SPANISH_PROBLEMS.alreadyClosed;
  if (code === 'day_not_closed') return SPANISH_PROBLEMS.notClosed;
  return action === 'close' ? SPANISH_PROBLEMS.closeFailed : SPANISH_PROBLEMS.reopenFailed;
}

const EN: BusinessDayCloseCopy = {
  sectionTitle: 'Business Day Close',
  sectionDescription:
    'Resolve ordinary tabs and payments, review the drawer reconciliation, then close the business day so its figures stop changing. An explicitly across-day tab for a known customer remains open.',
  loading: 'Loading…',
  businessDayLabel: 'Business Day',
  currentDay: 'Current',
  priorDayHint: 'Reviewing a prior business day. Closing it does not close the current business day.',
  daysLoadFailed: {
    title: 'Business Days Could Not Be Loaded',
    message: 'No business day can be closed until the list is available. Check the connection and try again.',
  },
  status: { open: 'Open', closed: 'Closed', reopened: 'Reopened' },
  closedAt: (at) => `Closed ${at}`,
  dayWindow:
    'The business day ends at 4:00 AM. Activity after midnight remains on the prior business day until then.',
  closeAction: 'Close Business Day',
  reopenedTitle: 'Business Day Reopened',
  drawers: 'Cash Drawers',
  noneOpened: 'None opened',
  drawerSummary: (reconciled, open) => `${reconciled} reconciled${open ? `, ${open} still open` : ''}`,
  variance: 'Drawer Variance',
  nothingReconciled: 'Nothing reconciled yet',
  nothingReconciledHint: 'No cash drawer has been closed, so there is no variance to review.',
  someReconciledHint: (reconciled) =>
    `Only ${reconciled} ${reconciled === 1 ? 'cash drawer is' : 'cash drawers are'} reconciled; the rest are still open.`,
  allReconciledHint: 'Every cash drawer is reconciled, with each currency shown separately.',
  rounding: 'Cash Rounding',
  roundingHint: 'Cash received above the bill when rounding to a note. It is neither tax nor a discount.',
  exact: 'Exact',
  short: 'short',
  over: 'over',
  notReconciled: 'Not reconciled',
  drawerCloseRequired: 'Drawer Close Required',
  drawerCloseRequiredBody: (open) =>
    `${open} ${open === 1 ? 'cash drawer is' : 'cash drawers are'} still open. Close and reconcile ${open === 1 ? 'it' : 'them'} before closing the business day.`,
  guestWorkRequired: 'Tabs or Payments Still Open',
  guestWorkRequiredBody: (ordinaryTabs, awaitingPayment) =>
    `${ordinaryTabs} ${ordinaryTabs === 1 ? 'ordinary tab is' : 'ordinary tabs are'} still open and ${awaitingPayment} ${awaitingPayment === 1 ? 'bill is' : 'bills are'} awaiting payment. Resolve them before closing. Only an explicitly across-day tab for a known customer may remain open.`,
  confirmTitle: (date) => `Business Day Close — ${date}`,
  confirmBody:
    'No new sale, payment, bill, or cash drawer can be added to this business day afterward. Work completed while a terminal was offline can still arrive when it synchronizes, and issued invoices are still recorded.',
  confirmReopen:
    'An explicitly across-day tab for a known customer remains open. You can reopen the business day later with a recorded reason.',
  confirmAction: 'Close Business Day',
  confirmCancel: 'Keep Open',
  closedTitle: 'Business Day Closed',
  closedBody:
    'Nothing new can be added to this business day. If a missed bill or incorrect payment belongs here, reopen it and record why.',
  reopenReason: 'Reopen Reason',
  reopenReasonDescription: 'Saved with the business day for the accounting record.',
  reopenAction: 'Reopen Business Day',
  lateTitle: 'Changes After Close',
  lateDescription:
    'Offline work performed before close and issued invoices can still arrive afterward. Review these entries to see whether the finalized figures changed.',
  arrivedLate: 'Performed before close and synchronized afterward',
  recordedAfterClose: 'Recorded after close',
  lateMeta: (state, by, at) => `${state}${by ? ` · ${by}` : ''} · ${at}`,
  landedWhat: (what) => what,
  closedNotice: {
    title: 'Business Day Closed',
    message: 'No new activity can be added until it is reopened.',
  },
  reopenedNotice: {
    title: 'Business Day Reopened',
    message: 'The reason was saved with the business day.',
  },
  problem: englishProblem,
};

const SPANISH_WHAT: Record<string, string> = {
  'a change': 'un cambio',
  'another drawer': 'otra caja',
  'another tab': 'otra cuenta',
  'another bill': 'otra cuenta de consumo',
  'another payment': 'otro pago',
  'an account charge': 'un cargo a cuenta',
  'an account payment': 'un pago de cuenta',
  'this invoice': 'esta factura',
  'a delivery order': 'un pedido de entrega',
  'this correction to a carrier’s crossing bill': 'esta corrección a la factura de transporte',
  'this correction to somebody’s hours': 'esta corrección a las horas de un empleado',
};

const ES: BusinessDayCloseCopy = {
  sectionTitle: 'Cierre del día de operación',
  sectionDescription:
    'Resolvé las cuentas ordinarias y sus pagos, revisá la conciliación de las cajas y luego cerrá el día de operación para que sus cifras dejen de cambiar. Una cuenta entre días asignada expresamente a un cliente conocido permanece abierta.',
  loading: 'Cargando…',
  businessDayLabel: 'Día de operación',
  currentDay: 'Actual',
  priorDayHint:
    'Estás revisando un día de operación anterior. Cerrarlo no cierra el día de operación actual.',
  daysLoadFailed: {
    title: 'No se pudieron cargar los días de operación',
    message:
      'No se puede cerrar ningún día de operación hasta que la lista esté disponible. Revisá la conexión e intentá de nuevo.',
  },
  status: { open: 'Abierto', closed: 'Cerrado', reopened: 'Reabierto' },
  closedAt: (at) => `Cerrado ${at}`,
  dayWindow:
    'El día de operación termina a las 4:00 a. m. La actividad después de medianoche permanece en el día anterior hasta esa hora.',
  closeAction: 'Cerrar día de operación',
  reopenedTitle: 'Día de operación reabierto',
  drawers: 'Cajas',
  noneOpened: 'Ninguna abierta',
  drawerSummary: (reconciled, open) =>
    `${reconciled} ${reconciled === 1 ? 'conciliada' : 'conciliadas'}${open ? `, ${open} ${open === 1 ? 'sigue abierta' : 'siguen abiertas'}` : ''}`,
  variance: 'Diferencia de caja',
  nothingReconciled: 'Ninguna conciliada todavía',
  nothingReconciledHint: 'No se ha cerrado ninguna caja, así que todavía no hay una diferencia que revisar.',
  someReconciledHint: (reconciled) =>
    `Solo ${reconciled} ${reconciled === 1 ? 'caja está conciliada' : 'cajas están conciliadas'}; las demás siguen abiertas.`,
  allReconciledHint: 'Todas las cajas están conciliadas y cada moneda se muestra por separado.',
  rounding: 'Redondeo en efectivo',
  roundingHint:
    'Efectivo recibido por encima de la cuenta al redondear a un billete. No es impuesto ni descuento.',
  exact: 'Exacto',
  short: 'faltante',
  over: 'sobrante',
  notReconciled: 'Sin conciliar',
  drawerCloseRequired: 'Cierre de caja pendiente',
  drawerCloseRequiredBody: (open) =>
    `${open} ${open === 1 ? 'caja sigue abierta' : 'cajas siguen abiertas'}. ${open === 1 ? 'Cerrala y conciliala' : 'Cerralas y concilialas'} antes de cerrar el día de operación.`,
  guestWorkRequired: 'Hay cuentas o pagos pendientes',
  guestWorkRequiredBody: (ordinaryTabs, awaitingPayment) =>
    `${ordinaryTabs} ${ordinaryTabs === 1 ? 'cuenta ordinaria sigue abierta' : 'cuentas ordinarias siguen abiertas'} y ${awaitingPayment} ${awaitingPayment === 1 ? 'cuenta de consumo espera' : 'cuentas de consumo esperan'} el pago. Resolvelas antes de cerrar. Solo puede quedar abierta una cuenta entre días asignada expresamente a un cliente conocido.`,
  confirmTitle: (date) => `Cierre del día de operación — ${date}`,
  confirmBody:
    'Después no se podrán agregar nuevas ventas, pagos, cuentas de consumo ni cajas a este día de operación. El trabajo realizado mientras una terminal estaba sin conexión puede llegar cuando se sincronice y las facturas emitidas todavía se registran.',
  confirmReopen:
    'Una cuenta entre días asignada expresamente a un cliente conocido permanece abierta. Podés reabrir el día de operación después y registrar el motivo.',
  confirmAction: 'Cerrar día de operación',
  confirmCancel: 'Mantener abierto',
  closedTitle: 'Día de operación cerrado',
  closedBody:
    'No se puede agregar nada nuevo a este día de operación. Si una cuenta de consumo omitida o un pago incorrecto pertenece aquí, reabrilo y registrá el motivo.',
  reopenReason: 'Motivo de reapertura',
  reopenReasonDescription: 'Se guarda con el día de operación para el registro contable.',
  reopenAction: 'Reabrir día de operación',
  lateTitle: 'Cambios después del cierre',
  lateDescription:
    'El trabajo sin conexión realizado antes del cierre y las facturas emitidas pueden llegar después. Revisá estas entradas para saber si cambiaron las cifras finalizadas.',
  arrivedLate: 'Realizado antes del cierre y sincronizado después',
  recordedAfterClose: 'Registrado después del cierre',
  lateMeta: (state, by, at) => `${state}${by ? ` · ${by}` : ''} · ${at}`,
  landedWhat: (what) => {
    const namedTab = /^a tab for (.+)$/i.exec(what);
    return namedTab ? `una cuenta para ${namedTab[1]}` : (SPANISH_WHAT[what] ?? 'un cambio');
  },
  closedNotice: {
    title: 'Día de operación cerrado',
    message: 'No se puede agregar actividad nueva hasta reabrirlo.',
  },
  reopenedNotice: {
    title: 'Día de operación reabierto',
    message: 'El motivo se guardó con el día de operación.',
  },
  problem: spanishProblem,
};

export function businessDayCloseCopy(locale: PortalLocale): BusinessDayCloseCopy {
  return locale === 'es-HN' ? ES : EN;
}
